import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

// Helper function to get query parameters from URL
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const SetPassword = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const token = query.get('token');
  const email = query.get('email');

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: '',
    isValid: false,
    color: 'gray'
  });

  // Validate the invite token when component mounts
  useEffect(() => {
    const validateInvite = async () => {
      if (!token || !email) {
        setError('Invalid invitation link. Please check the URL or contact your administrator.');
        setValidating(false);
        setLoading(false);
        return;
      }

      try {
        const res = await axios.post('/api/auth/validate-invite', { token, email });
        setInviteData(res.data);
        setValidating(false);
        setLoading(false);
      } catch (err) {
        console.error('Invite validation error:', err);
        setError(err.response?.data?.message || 'Invalid or expired invitation link');
        setValidating(false);
        setLoading(false);
      }
    };

    validateInvite();
  }, [token, email]);

  // Password validation requirements
  const minLength = 8;
  const hasUppercase = /[A-Z]/.test(formData.password);
  const hasLowercase = /[a-z]/.test(formData.password);
  const hasNumbers = /\d/.test(formData.password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);
  
  // Update password strength when password changes
  useEffect(() => {
    validatePassword(formData.password);
  }, [formData.password]);
  
  // Password validation function
  const validatePassword = (password) => {
    // Empty password
    if (!password) {
      setPasswordStrength({
        score: 0,
        message: '',
        isValid: false,
        color: 'gray'
      });
      return;
    }
    
    // Calculate password strength
    let score = 0;
    const checks = [
      password.length >= minLength,
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /\d/.test(password),
      /[!@#$%^&*(),.?":{}|<>]/.test(password)
    ];
    
    score = checks.filter(Boolean).length;
    
    // Set password strength message and color
    let message = '';
    let color = '';
    let isValid = false;
    
    // Check if we have the minimum requirements (8 chars, uppercase, lowercase, number)
    const hasMinRequirements = 
      password.length >= minLength && 
      /[A-Z]/.test(password) && 
      /[a-z]/.test(password) && 
      /\d/.test(password);
    
    if (score <= 2) {
      message = 'Weak password';
      color = 'red';
    } else if (score === 3) {
      // Check if the 3 requirements include our minimum set
      if (hasMinRequirements) {
        message = 'Good password';
        color = 'blue';
        isValid = true;
      } else {
        message = 'Fair password';
        color = 'orange';
      }
    } else if (score === 4) {
      message = 'Good password';
      color = 'blue';
      isValid = true;
    } else if (score === 5) {
      message = 'Strong password';
      color = 'green';
      isValid = true;
    }
    
    setPasswordStrength({ score, message, isValid, color });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate password strength
    if (!passwordStrength.isValid) {
      setError('Please use a stronger password. Your password must be at least 8 characters and include uppercase letters, lowercase letters, and numbers. Special characters are recommended for increased security.');
      setLoading(false);
      return;
    }

    // Confirm passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      // Submit password to server
      const res = await axios.post('/api/auth/set-password', {
        token,
        email,
        password: formData.password,
      });

      // Save token and redirect
      localStorage.setItem('token', res.data.token);
      
      // Also save user data from response, with modified onboarding flags to skip the wizard
      if (res.data.user) {
        // Mark onboarding as complete for invited users to bypass the wizard
        const userData = {
          ...res.data.user,
          onboarding_complete: true,
          company_onboarding_complete: true
        };
        localStorage.setItem('user', JSON.stringify(userData));
      }
      
      toast.success('Welcome! Your password has been set successfully.');
      
      // Redirect to root path (main dashboard)
      navigate('/');
    } catch (err) {
      console.error('Password setup error:', err);
      setError(err.response?.data?.message || 'Error setting password. Please try again.');
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <div className="text-center">
            <svg className="animate-spin mx-auto h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="mt-3 text-gray-600">Validating your invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Invitation Error</h3>
            <p className="mt-2 text-gray-600">{error}</p>
            <div className="mt-6">
              <button
                onClick={() => navigate('/login')}
                className="w-full inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Set Your Password</h2>
          <p className="mt-2 text-gray-600">
            Welcome to {inviteData?.company?.name}! Please set your password to complete your account setup.
          </p>
        </div>

        {inviteData && (
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <p className="text-sm text-blue-800">
              <strong>Name:</strong> {inviteData.user.name}<br />
              <strong>Email:</strong> {inviteData.user.email}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleChange}
                className={`mt-1 block w-full py-2 px-3 border ${formData.password ? `border-${passwordStrength.color}-500` : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                placeholder="Create a strong password"
              />
              {formData.password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium text-${passwordStrength.color}-500`}>
                      {passwordStrength.message}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full bg-${passwordStrength.color}-500`} 
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    />
                  </div>
                  <ul className="mt-2 text-xs text-gray-600 space-y-1">
                    <li className={`flex items-center ${formData.password.length >= minLength ? 'text-green-500' : ''}`}>
                      <span>{formData.password.length >= minLength ? '✓' : '○'}</span>
                      <span className="ml-1">Minimum 8 characters</span>
                    </li>
                    <li className={`flex items-center ${hasUppercase ? 'text-green-500' : ''}`}>
                      <span>{hasUppercase ? '✓' : '○'}</span>
                      <span className="ml-1">At least one uppercase letter</span>
                    </li>
                    <li className={`flex items-center ${hasLowercase ? 'text-green-500' : ''}`}>
                      <span>{hasLowercase ? '✓' : '○'}</span>
                      <span className="ml-1">At least one lowercase letter</span>
                    </li>
                    <li className={`flex items-center ${hasNumbers ? 'text-green-500' : ''}`}>
                      <span>{hasNumbers ? '✓' : '○'}</span>
                      <span className="ml-1">At least one number</span>
                    </li>
                    <li className={`flex items-center ${hasSpecialChar ? 'text-green-500' : ''}`}>
                      <span>{hasSpecialChar ? '✓' : '○'}</span>
                      <span className="ml-1">At least one special character (recommended)</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Confirm your password"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-400 rounded p-3">
                <p className="text-red-500 text-sm">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  loading
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                }`}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  'Set Password & Login'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetPassword;